import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core'
import { TraceService } from '@sentry/angular'
import { environment } from './environments/environment'
import { AppComponent } from './app/app.component'
import { ServiceWorkerModule } from '@angular/service-worker'
import { Drivers } from '@ionic/storage'
import { IonicStorageModule } from '@ionic/storage-angular'
import { provideTranslateService } from '@ngx-translate/core'
import { withInterceptorsFromDi, provideHttpClient, withXhr } from '@angular/common/http'
import { routes } from './app/app-routing'
import { bootstrapApplication } from '@angular/platform-browser'
import { StateService } from './app/services/state.service'
import { FirebaseAnalyticsService } from './app/services/firebase-analytics.service'
import { StorageService } from './app/services/storage.service'
import { provideIonicAngular, IonicRouteStrategy } from '@ionic/angular'
import { RouteReuseStrategy, Router, provideRouter } from '@angular/router'
import { injectSpeedInsights } from '@vercel/speed-insights'
import { isNoMonitorAndProd } from './app/helpers/check-for-monitor-and-prod.helper'
import { Capacitor } from '@capacitor/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common'
import {
  handleBootstrapError,
  initializeSentry,
  provideSentryErrorHandler,
} from './app/helpers/sentry-bootstrap.helper'
import vercelConfig from '../vercel.json'

if (isNoMonitorAndProd() && injectSpeedInsights && !Capacitor.isNativePlatform()) {
  try {
    injectSpeedInsights()
  } catch (error) {
    // do nothing
  }
}

initializeSentry(environment)

if (environment.production) {
  enableProdMode()
}

// reload page if error is "isProxied" and browser is Safari
if (window && navigator?.userAgent?.indexOf('Safari') > -1) {
  window.onunhandledrejection = (event) => {
    if (event?.reason?.message?.includes('isProxied')) {
      location.reload()
    }
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(
      IonicStorageModule.forRoot({
        name: '__pregnantdb',
        driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      }),
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    ),
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    provideSentryErrorHandler(),
    {
      provide: TraceService,
      deps: [Router],
    },
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({ prefix: './i18n/', suffix: '.json', enforceLoading: true }),
    }),
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        const width = config.width ?? 1080
        const vercelImageSizes = vercelConfig?.images?.sizes
        let resizeSize = null
        if (vercelImageSizes) {
          for (const size of vercelImageSizes) {
            if (width <= size) {
              resizeSize = size
              break
            }
          }
        }
        // if native platform, return the original src
        if (Capacitor.isNativePlatform()) {
          return config.src
        }
        // if web and not development, use Vercel Image Optimization, otherwise return the original src
        return environment.env !== 'development'
          ? `/_vercel/image?url=${encodeURIComponent(config.src)}&w=${resizeSize ?? 3840}&q=75`
          : config.src
      },
    },
    provideAppInitializer(async () => {
      const storageService = inject(StorageService)
      const firebaseAnalyticsService = inject(FirebaseAnalyticsService)
      const stateService = inject(StateService)
      inject(TraceService)
      await storageService.init()
      await firebaseAnalyticsService.init()
      await stateService.init()
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideIonicAngular(),
    provideRouter(routes),
  ],
}).catch(handleBootstrapError)
